'use client';

import React, {useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {createPortal} from 'react-dom';
import {X} from 'lucide-react';

import {cn} from '@/libs/utils';
import {useModalStore} from '@/presentation/hooks/useStores';
import type {ModalConfig, ModalSize} from '@/presentation/stores/ModalStore';

interface ModalRendererProps {
    modal: ModalConfig;
    onClose: (id: string) => void;
    onBackdropClick: (id: string) => void;
}

const ModalRenderer = observer(({modal, onClose, onBackdropClick}: ModalRendererProps) => {
    const {component: Component, props = {}, options = {}} = modal;

    const sizeClasses: Record<ModalSize, string> = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        full: 'max-w-full w-full h-full',
    };

    const size = options.size || 'md';

    return (
        <div
            className={cn(
                'fixed inset-0 z-50 flex items-center justify-center p-4',
                options.overlayClassName
            )}
            style={{zIndex: modal.zIndex}}
        >
            {/* Backdrop */}
            {options.backdrop !== false && (
                <div
                    className="fixed inset-0 bg-black/80 transition-opacity"
                    onClick={() => onBackdropClick(modal.id)}
                />
            )}

            {/* Modal Content */}
            <div
                className={cn(
                    'relative bg-white rounded-lg shadow-lg max-h-[90vh] overflow-auto',
                    'animate-in fade-in-0 zoom-in-95 duration-200',
                    sizeClasses[size],
                    size === 'full' && 'rounded-none',
                    options.className
                )}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                {options.dismissible !== false && (
                    <button
                        onClick={() => onClose(modal.id)}
                        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none z-10"
                    >
                        <X className="h-4 w-4"/>
                        <span className="sr-only">Close</span>
                    </button>
                )}

                {/* Modal Component */}
                <Component
                    {...props}
                    onClose={() => onClose(modal.id)}
                    modalId={modal.id}
                />
            </div>
        </div>
    );
});

const GlobalModalRenderer = observer(() => {
    const modalStore = useModalStore();

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                modalStore.handleEscapeKey();
            }
        };

        if (modalStore.hasActiveModals) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [modalStore, modalStore.hasActiveModals]);

    // Clean up body overflow on unmount
    useEffect(() => {
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    if (!modalStore.hasActiveModals) {
        return null;
    }

    // Only render if we're on the client side
    if (typeof window === 'undefined') {
        return null;
    }

    return createPortal(
        <>
            {modalStore.allModals.map((modal) => (
                <ModalRenderer
                    key={modal.id}
                    modal={modal}
                    onClose={(id) => modalStore.closeModal(id)}
                    onBackdropClick={(id) => modalStore.handleBackdropClick(id)}
                />
            ))}
        </>,
        document.body
    );
});

interface ModalProviderProps {
    children: React.ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = observer(({children}) => {
    return (
        <>
            {children}
            <GlobalModalRenderer/>
        </>
    );
});

ModalProvider.displayName = 'ModalProvider';

export default ModalProvider;
